import Formatters from '../components/grid-addons/formatters';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import DateInputEditor from '../components/DateInputEditor/DateInputEditor';
import { IEditableDataGridColumnDef } from '../@types/editableDataGridTypes';
import { useValueState } from './hooks';
import ConnectedFormContext from '../components/Context/ConnectedFormContext';
import { useDispatch } from 'react-redux';
import { serviceActions } from '../state/modules/service';
import { AlertType, ILotAttributeConfig } from '../graphql/schema/p4types';
import { cloneDeep } from '@apollo/client/utilities';
import { Button, Field, Popover, PopoverSurface, PopoverTrigger, Text } from '@fluentui/react-components';
import Icon from '@mdi/react';
import { mdiContentDuplicate } from '@mdi/js';
import theme from '../styles/themes/default/defaultTheme';
import { BasicHeader } from '../actions/BasicHeader';
import QueryDropdown from '../components/connectedForm/QueryDropdown';
import { BinLocation } from '../graphql/entities';
import * as input from '../components/connectedForm/inputComponents';
import { BlurTextFieldType } from '../components/connectedForm/BlurTextField/BlurTextField';
import { ModalBottomButtonContainer } from '../components/modals/Styles';
import { PrimaryButton } from '../components/connectedForm/Button';

export const LotAttributeBulkEntryHeader = ({
  currentRowIdx,
  column,
  attributePath,
}: {
  currentRowIdx?: number;
  column?: IEditableDataGridColumnDef; //Dynamically added.
  attributePath: string;
}) => {
  const [binLocation, setBinLocation] = useState(null);
  const [bulkEntryValues, setBulkEntryValues] = useValueState('');
  const { values, change } = useContext(ConnectedFormContext);
  const dispatch = useDispatch();

  const attributeLines = useMemo(() => {
    return (values as any)[attributePath];
  }, [attributePath, values]);

  const initialBinLocation = useMemo(() => attributeLines?.[0]?.binLocation || null, [attributeLines]);
  useEffect(() => {
    if (initialBinLocation) {
      setBinLocation(initialBinLocation);
    }
  }, [initialBinLocation]);

  const onClick = useCallback(() => {
    if (!binLocation) {
      dispatch(
        serviceActions.genericAlert('Bin Location', 'Please select bin location for bulk entry', AlertType.Info)
      );
      return;
    }

    //Filter out any no-bin/no-quantity attribute lines at this point - Otherwise can get stuck on goods receipt form which doesn't filter them out later
    // (Purchase Order receipt filters them out on save anyway).
    const newAttributeLines = cloneDeep(attributeLines ?? []).filter(
      (attributeLine: any) => attributeLine.binLocation && attributeLine.quantity > 0
    );

    //Split and remove any empty entries (Expecting at least one if user hits enter after last entry).
    const splitValues = bulkEntryValues.split(/\r?\n/).filter((s) => !!s && s.length > 0);
    const deDuplicatedValues = [...new Set(splitValues)];
    if (deDuplicatedValues.length < splitValues.length) {
      const dupeCount = splitValues.length - deDuplicatedValues.length;
      dispatch(
        serviceActions.genericAlert(
          'De-duplication',
          `${dupeCount} duplicated entries have been removed.`,
          AlertType.Info
        )
      );
    }

    deDuplicatedValues.forEach((newLotAttributeValue) => {
      newAttributeLines.push({
        binLocation: binLocation,
        quantity: 1,
        [column!.key!]: newLotAttributeValue,
      });
    });

    //Inject the additional lines
    dispatch(change(attributePath, newAttributeLines));
    setBulkEntryValues('');
  }, [attributeLines, attributePath, binLocation, bulkEntryValues, change, column, dispatch, setBulkEntryValues]);

  return (
    <span style={{ paddingTop: '10px', paddingLeft: '5px', verticalAlign: 'middle' }}>
      <Text>{column!.name}</Text>
      <Popover positioning={'below'}>
        <PopoverTrigger disableButtonEnhancement>
          <Button
            icon={<Icon path={mdiContentDuplicate} size={1.25} color={theme.colors.grey120} />}
            style={{ float: 'right' }}
          />
        </PopoverTrigger>
        <PopoverSurface style={{ padding: '0 0 60px 0' }}>
          <BasicHeader>{column!.name}</BasicHeader>
          <div
            style={{
              width: '280px',
              height: '252px',
              padding: '1em',
              position: 'relative',
              overflowY: 'scroll',
            }}
          >
            <Field label={'Bin Location'} size={'small'}>
              <QueryDropdown
                {...BinLocation.dropdown}
                includeLabel={false}
                label="Bin Location"
                value={binLocation}
                onChange={setBinLocation}
                data-testid="bulk-entry-bin-location"
              />
            </Field>
            <Field label={'Values'} size={'small'}>
              <input.BlurTextField
                type={BlurTextFieldType.TextArea}
                includeLabel={false}
                data-testid="bulk-entry-serial-numbers"
                value={bulkEntryValues}
                onChange={setBulkEntryValues}
                onBlur={setBulkEntryValues}
                enableOnChange={true}
                height={'136px'}
              />
            </Field>
          </div>
          <ModalBottomButtonContainer>
            <PrimaryButton small onClick={onClick} data-testid={`lot-attribute-bulk-insert`}>
              Enter
            </PrimaryButton>
          </ModalBottomButtonContainer>
        </PopoverSurface>
      </Popover>
    </span>
  );
};

const getLotAttributeColumns = (
  lotAttributes: Array<ILotAttributeConfig> | IterableIterator<ILotAttributeConfig>,
  attributePath: string,
  currentRowIdx?: number
): { attrs: Array<ILotAttributeConfig>; columns: Array<IEditableDataGridColumnDef>; attributePath: string } => {
  const columns: Array<IEditableDataGridColumnDef> = [];

  const attrs = [...lotAttributes];
  if (attrs.length > 0) {
    attrs.sort((a, b) => a.sortOrder - b.sortOrder);
    attrs.forEach((attr) => {
      let column: IEditableDataGridColumnDef | null = null;
      const key = getKey(attr);
      switch (attr.attributeType) {
        case 'TEXT':
          column = {
            name: attr.title,
            key: key,
            editable: true,
            resizable: true,
            headerRenderer: <LotAttributeBulkEntryHeader currentRowIdx={currentRowIdx} attributePath={attributePath} />,
          };
          break;
        case 'DATE':
          column = {
            name: attr.title,
            key: key,
            editable: true,
            resizable: true,
            formatter: Formatters.DateFormatter,
            editor: <DateInputEditor />,
          };
          break;
        case 'INTEGER':
          column = {
            name: attr.title,
            key: key,
            editable: true,
            resizable: true,
            formatter: Formatters.NumericFormatter,
            options: {
              format: { decimals: 0 },
            },
            cellClass: 'rightAlign',
            headerRenderer: <LotAttributeBulkEntryHeader currentRowIdx={currentRowIdx} attributePath={attributePath} />,
          };
          break;
        case 'NUMERIC':
          column = {
            name: attr.title,
            key: key,
            editable: true,
            formatter: Formatters.NumericFormatter,
            options: {
              format: { decimals: 2 },
            },
            resizable: true,
            cellClass: 'rightAlign',
            headerRenderer: <LotAttributeBulkEntryHeader currentRowIdx={currentRowIdx} attributePath={attributePath} />,
          };
          break;
        case 'SEQUENTIAL':
        case 'GRN':
          column = {
            name: attr.title,
            key: key,
            editable: false,
            resizable: true,
          };
          break;
        default:
          break;
      }
      if (column) columns.push(column);
    });
  }

  return { columns, attrs };
};

type ILotAttributeKey = `lotAttributes[${number | 'null'}][${string}][${number}][${
  | 'textValue'
  | 'dateValue'
  | 'integerValue'
  | 'doubleValue'
  | 'null'}]`;

export const getKey = (attribute: ILotAttributeConfig): ILotAttributeKey => {
  switch (attribute.attributeType) {
    case 'TEXT':
      return `lotAttributes[${attribute.id}][${attribute.uuid}][${attribute.concurrencyVersion}][textValue]`;
    case 'DATE':
      return `lotAttributes[${attribute.id}][${attribute.uuid}][${attribute.concurrencyVersion}][dateValue]`;
    case 'INTEGER':
      return `lotAttributes[${attribute.id}][${attribute.uuid}][${attribute.concurrencyVersion}][integerValue]`;
    case 'NUMERIC':
      return `lotAttributes[${attribute.id}][${attribute.uuid}][${attribute.concurrencyVersion}][doubleValue]`;
    default:
      return `lotAttributes[${attribute.id}][${attribute.uuid}][${attribute.concurrencyVersion}][null]`;
  }
};

export function getAttrIdArray(
  key: string
): [
  initial: 'lotAttributes',
  id: `${number}` | 'null',
  uuid: string,
  concurrencyVersion: `${number}`,
  valueFieldName: 'textValue' | 'dateValue' | 'integerValue' | 'doubleValue' | 'null'
] {
  return key.match(/\[(.*?)\]\[(.*?)\]\[(.*?)\]\[(.*?)\]/) as any;
}

export default getLotAttributeColumns;
